import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Filter, RefreshCw, Eye, CalendarClock } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { formatDate, formatTime, todayISO } from "@/lib/dateTime";
import { canCR } from "@/lib/permissions";
import { DiscardConfirm } from "@/components/DiscardConfirm";
import { useColumnManager } from "@/hooks/useColumnManager";
import { ColumnModals } from "@/components/ColumnModals";
import { ColumnToolbar } from "@/components/ColumnToolbar";
import {
  DEFAULT_APPOINTMENT_COLUMNS,
  CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS,
  getAppointmentColumns,
} from "@/lib/appointmentColumns";

const N1_OPTIONS = ["Confirmed", "Pending", "Not Reachable", "Rescheduled", "Cancelled"];

// Shared column set for Tomorrow / Future. Status = Booking Status
// (appointment_status) on both.
const UPCOMING_COLUMNS = getAppointmentColumns("Booking Status");

const getN1Color = (val) => {
  const map = {
    Confirmed: "bg-green-100 text-green-800",
    Pending: "bg-yellow-100 text-yellow-800",
    "Not Reachable": "bg-orange-100 text-orange-800",
    Rescheduled: "bg-blue-100 text-blue-800",
    Cancelled: "bg-red-100 text-red-800",
  };
  return map[val] || "bg-gray-100 text-gray-800";
};

// (Column definitions moved to shared UPCOMING_COLUMNS above.)

const Upcoming = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState(null);
  const [submittingN1, setSubmittingN1] = useState(false);
  const submittingN1Ref = useRef(false);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appointments, setAppointments] = useState([]);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ dateRange: "", service_type: "", sa: "", source: "", status: "", branch: "" });

  // Two independent per-tab preference stores. Hiding a column on Tomorrow
  // does NOT hide it on Future.
  const tmrColMgr = useColumnManager(
    "upcoming_tomorrow",
    UPCOMING_COLUMNS,
    CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS
  );
  const futColMgr = useColumnManager(
    "upcoming_future",
    UPCOMING_COLUMNS,
    CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS
  );

  // N-1 popup state
  const [n1Popup, setN1Popup] = useState({ open: false, appointmentId: null, status: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" });
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  // Matrix permissions — inline N-1/docket actions need upcoming.update;
  // New Appointment shows with update on Today OR Upcoming.
  const canUpdateUpcoming = canCR(user, "upcoming", "update");
  const canCreateAppt = canCR(user, "today", "update") || canCR(user, "upcoming", "update");

  // Get all reschedule history entries
  const getRescheduleEntries = (history) => (history || []).filter(h => h.from_date);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [apptsRes, settingsRes, branchesRes] = await Promise.all([
        axios.get(`${API}/appointments?view=upcoming`, { withCredentials: true }),
        axios.get(`${API}/settings`, { withCredentials: true }),
        axios.get(`${API}/branches`, { withCredentials: true }),
      ]);
      setAppointments(apptsRes.data || []);
      setSettings(settingsRes.data);
      setBranches(branchesRes.data || []);
    } catch {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (rows) => {
    return rows.filter((r) => {
      if (filters.service_type && r.service_type !== filters.service_type) return false;
      if (filters.sa && r.allocated_sa !== filters.sa) return false;
      if (filters.source && r.source !== filters.source) return false;
      if (filters.status && r.appointment_status !== filters.status) return false;
      return true;
    });
  };

  const now = new Date();
  const todayStr = todayISO();
  const tmrDate = new Date(now); tmrDate.setDate(tmrDate.getDate() + 1);
  const tmrStr = `${tmrDate.getFullYear()}-${String(tmrDate.getMonth() + 1).padStart(2, "0")}-${String(tmrDate.getDate()).padStart(2, "0")}`;

  const tomorrowRows = appointments.filter((r) => r.appointment_date === tmrStr);
  let futureRows = appointments.filter((r) => r.appointment_date > tmrStr);

  // Apply date-range filter
  if (filters.dateRange) {
    const makeFutureDate = (offset) => {
      const d = new Date(now); d.setDate(d.getDate() + offset);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    };
    const rangeMap = { day_after: 2, "3days": 4, "7days": 8, "14days": 15, "30days": 31 };
    if (filters.dateRange === "this_month") {
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const endStr = `${endOfMonth.getFullYear()}-${String(endOfMonth.getMonth() + 1).padStart(2, "0")}-${String(endOfMonth.getDate()).padStart(2, "0")}`;
      futureRows = futureRows.filter((r) => r.appointment_date <= endStr);
    } else if (rangeMap[filters.dateRange]) {
      const endStr = makeFutureDate(rangeMap[filters.dateRange]);
      futureRows = futureRows.filter((r) => r.appointment_date <= endStr);
    }
  }
  futureRows = applyFilters(futureRows);

  const handleN1Change = (id, val) => {
    if (val === "Rescheduled" || val === "Cancelled") {
      setN1Popup({ open: true, appointmentId: id, status: val, rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" });
    } else {
      updateN1Direct(id, { n_minus_1_confirmation_status: val });
    }
  };

  const updateN1Direct = async (id, data) => {
    try {
      await axios.put(`${API}/appointments/${id}`, data, { withCredentials: true });
      setAppointments((prev) => prev.map((r) => r.appointment_id === id ? { ...r, ...data } : r));
      toast.success("N-1 status updated");
    } catch {
      toast.error("Failed to update N-1 status");
    }
  };

  const submitN1Popup = async () => {
    if (submittingN1Ref.current) return;
    const { appointmentId, status, rescheduleDate, rescheduleRemarks, cancelReason } = n1Popup;
    const data = { n_minus_1_confirmation_status: status };
    if (status === "Rescheduled") {
      if (!rescheduleDate) { toast.error("Please select a reschedule date"); return; }
      data.reschedule_date = rescheduleDate;
      data.reschedule_remarks = rescheduleRemarks;
    } else if (status === "Cancelled") {
      if (!cancelReason) { toast.error("Please enter a cancellation reason"); return; }
      data.cancel_reason = cancelReason;
      data.appointment_status = "Cancelled";
    }
    setSubmittingN1(true);
    submittingN1Ref.current = true;
    try {
      await updateN1Direct(appointmentId, data);
      setN1Popup({ open: false, appointmentId: null, status: "", rescheduleDate: "", rescheduleRemarks: "", cancelReason: "" });
    } finally {
      setSubmittingN1(false);
      submittingN1Ref.current = false;
    }
  };
  const toggleDocket = async (id, current) => {
    try {
      await axios.put(`${API}/appointments/${id}`, { docket_readiness: !current }, { withCredentials: true });
      setAppointments((prev) => prev.map((r) => r.appointment_id === id ? { ...r, docket_readiness: !r.docket_readiness } : r));
    } catch {
      toast.error("Failed to update docket status");
    }
  };

  // Visible column lists (per-tab, independent)
  const visTmr = tmrColMgr.visibleCols;
  const visFut = futColMgr.visibleCols;

  // Shared cell fragments used by both renderers.
  const renderCustomerName = (row) => (
    <div className="flex items-center gap-1">
      <span className="text-sm font-medium">{row.customer_name}</span>
      {row.priority_customer && (
        <div className="w-4 h-4 bg-black text-white rounded-sm flex items-center justify-center text-[10px] font-bold shrink-0">P</div>
      )}
      {row.is_rescheduled && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <div className="w-4 h-4 bg-amber-100 text-amber-800 rounded-sm flex items-center justify-center text-[10px] font-bold shrink-0 cursor-help" data-testid={`reschedule-badge-${row.appointment_id}`}>R</div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-[240px] p-3 bg-gray-100 border-0">
              {(() => {
                const entries = getRescheduleEntries(row.reschedule_history);
                return entries.length > 0 ? (
                  <>
                    <p className="font-bold text-sm mb-2 text-black">{entries.length} reschedule{entries.length !== 1 ? "s" : ""}</p>
                    {entries.map((h, i) => (
                      <p key={i} className="text-sm text-black mb-1">&gt; {formatDate(h.from_date)}</p>
                    ))}
                  </>
                ) : (
                  <p className="text-sm text-black">Rescheduled</p>
                );
              })()}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );

  const renderRemarks = (row) => (
    row.specific_repair_request ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-xs font-medium cursor-help underline decoration-dotted">Yes</span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs"><p className="text-sm">{row.specific_repair_request}</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : <span className="text-xs text-gray-400">No</span>
  );

  const renderActions = (row) => (
    <Button variant="ghost" size="icon" className="h-8 w-8 rounded-sm" onClick={() => navigate(`/appointments/${row.appointment_id}`)} data-testid={`action-btn-${row.appointment_id}`}>
      <Eye className="w-4 h-4" strokeWidth={1.5} />
    </Button>
  );

  // Cell renderer for the Tomorrow tab. N-1 stays interactive here (this is
  // where the N-1 confirmation call actually happens). Docket readiness is
  // also togglable pre-visit. Status = Booking Status (appointment_status).
  const renderTmrCell = (colId, row) => {
    switch (colId) {
      case "time": return <span className="text-sm font-medium">{formatTime(row.appointment_time)}</span>;
      case "source": return <span className="text-sm">{row.source}</span>;
      case "customer_name": return renderCustomerName(row);
      case "phone": return <span className="text-sm">{row.customer_phone}</span>;
      case "mail_id": return <span className="text-sm">{row.customer_email || "-"}</span>;
      case "reg_no": return <span className="text-sm font-medium">{row.vehicle_reg_no || row.vehicle_reg || "-"}</span>;
      case "vin": return <span className="text-sm">{row.vin || "-"}</span>;
      case "model": return <span className="text-sm">{row.vehicle_model || row.model}</span>;
      case "current_km": return <span className="text-sm">{row.current_km || "-"}</span>;
      case "ots": return <span className="text-sm">{row.ots || row.ots_recall ? "Yes" : "No"}</span>;
      case "service_type": return <Badge variant="outline" className="rounded-sm text-xs font-medium whitespace-nowrap">{row.service_type}</Badge>;
      case "sa_name": return <span className="text-sm">{row.allocated_sa}</span>;
      case "docket_readiness":
        if (!canUpdateUpcoming) {
          return (
            <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${row.docket_readiness ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{row.docket_readiness ? "Yes" : "No"}</Badge>
          );
        }
        return (
          <button type="button" onClick={() => toggleDocket(row.appointment_id, row.docket_readiness)} className="cursor-pointer" data-testid={`docket-toggle-${row.appointment_id}`}>
            <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${row.docket_readiness ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{row.docket_readiness ? "Yes" : "No"}</Badge>
          </button>
        );
      case "n1":
        if (!canUpdateUpcoming) {
          return <Badge className={`${getN1Color(row.n_minus_1_confirmation_status)} rounded-sm text-xs font-medium border-0 whitespace-nowrap`}>{row.n_minus_1_confirmation_status || "Pending"}</Badge>;
        }
        return (
          <Select value={row.n_minus_1_confirmation_status || "Pending"} onValueChange={(v) => handleN1Change(row.appointment_id, v)}>
            <SelectTrigger className="h-7 w-[130px] rounded-sm text-xs border-0 p-0 pl-2 mx-auto" data-testid={`n1-select-${row.appointment_id}`}>
              <Badge className={`${getN1Color(row.n_minus_1_confirmation_status)} rounded-sm text-xs font-medium border-0 whitespace-nowrap`}>{row.n_minus_1_confirmation_status || "Pending"}</Badge>
            </SelectTrigger>
            <SelectContent>{N1_OPTIONS.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}</SelectContent>
          </Select>
        );
      case "status": return <Badge variant="outline" className="rounded-sm text-xs font-medium whitespace-nowrap">{row.appointment_status}</Badge>;
      case "cre_name": return <span className="text-sm">{row.cre_name || "-"}</span>;
      case "lost_customer":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${row.lost_customer ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>
            {row.lost_customer ? "Yes" : "No"}
          </Badge>
        );
      case "remarks": return renderRemarks(row);
      case "actions": return renderActions(row);
      default: return null;
    }
  };

  // Cell renderer for the Future tab. N-1 + docket_readiness are shown as
  // plain read-only badges — those actions only make sense on N-1 day.
  // Status = Booking Status (appointment_status).
  const renderFutCell = (colId, row) => {
    switch (colId) {
      case "time": return <span className="text-sm font-medium">{formatTime(row.appointment_time)}</span>;
      case "source": return <span className="text-sm">{row.source}</span>;
      case "customer_name": return renderCustomerName(row);
      case "phone": return <span className="text-sm">{row.customer_phone}</span>;
      case "mail_id": return <span className="text-sm">{row.customer_email || "-"}</span>;
      case "reg_no": return <span className="text-sm font-medium">{row.vehicle_reg_no || row.vehicle_reg || "-"}</span>;
      case "vin": return <span className="text-sm">{row.vin || "-"}</span>;
      case "model": return <span className="text-sm">{row.vehicle_model || row.model}</span>;
      case "current_km": return <span className="text-sm">{row.current_km || "-"}</span>;
      case "ots": return <span className="text-sm">{row.ots || row.ots_recall ? "Yes" : "No"}</span>;
      case "service_type": return <Badge variant="outline" className="rounded-sm text-xs font-medium whitespace-nowrap">{row.service_type}</Badge>;
      case "sa_name": return <span className="text-sm">{row.allocated_sa}</span>;
      case "docket_readiness":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${row.docket_readiness ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
            {row.docket_readiness ? "Yes" : "No"}
          </Badge>
        );
      case "n1": return <span className="text-xs">{row.n_minus_1_confirmation_status || "Pending"}</span>;
      case "status": return <Badge variant="outline" className="rounded-sm text-xs font-medium whitespace-nowrap">{row.appointment_status}</Badge>;
      case "cre_name": return <span className="text-sm">{row.cre_name || "-"}</span>;
      case "lost_customer":
        return (
          <Badge className={`rounded-sm text-xs font-medium border-0 whitespace-nowrap ${row.lost_customer ? "bg-red-100 text-red-800" : "bg-gray-100 text-gray-800"}`}>
            {row.lost_customer ? "Yes" : "No"}
          </Badge>
        );
      case "remarks": return renderRemarks(row);
      case "actions": return renderActions(row);
      default: return null;
    }
  };

  const updateFilter = (key, v) => {
    setFilters((prev) => ({ ...prev, [key]: v === "all" ? "" : v }));
  };

  // Filter Dialog
  const FilterDialog = () => (
    <Dialog open={filterOpen} onOpenChange={setFilterOpen}>
      <DialogContent className="rounded-sm max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-heading font-bold text-xl tracking-tight uppercase">
            Filters
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 pt-4">
          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Date Range
            </Label>
            <Select value={filters.dateRange || "all"} onValueChange={(v) => updateFilter("dateRange", v)}>
              <SelectTrigger className="rounded-sm" data-testid="upcoming-filter-date">
                <SelectValue placeholder="All Dates" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dates</SelectItem>
                <SelectItem value="day_after">Day After Tomorrow</SelectItem>
                <SelectItem value="3days">Next 3 Days</SelectItem>
                <SelectItem value="7days">Next 7 Days</SelectItem>
                <SelectItem value="14days">Next 14 Days</SelectItem>
                <SelectItem value="30days">Next 30 Days</SelectItem>
                <SelectItem value="this_month">This Month</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Service Type
            </Label>
            <Select value={filters.service_type || "all"} onValueChange={(v) => updateFilter("service_type", v)}>
              <SelectTrigger className="rounded-sm" data-testid="upcoming-filter-service-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {settings?.service_types?.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Service Advisor
            </Label>
            <Select value={filters.sa || "all"} onValueChange={(v) => updateFilter("sa", v)}>
              <SelectTrigger className="rounded-sm" data-testid="upcoming-filter-sa">
                <SelectValue placeholder="All SAs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All SAs</SelectItem>
                {settings?.service_advisors?.map((sa) => <SelectItem key={sa} value={sa}>{sa}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Source
            </Label>
            <Select value={filters.source || "all"} onValueChange={(v) => updateFilter("source", v)}>
              <SelectTrigger className="rounded-sm" data-testid="upcoming-filter-source">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                {settings?.sources?.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Status
            </Label>
            <Select value={filters.status || "all"} onValueChange={(v) => updateFilter("status", v)}>
              <SelectTrigger className="rounded-sm" data-testid="upcoming-filter-status">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Scheduled">Scheduled</SelectItem>
                <SelectItem value="Confirmed">Confirmed</SelectItem>
                <SelectItem value="Rescheduled">Rescheduled</SelectItem>
                <SelectItem value="Cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1.5 block">
              Branch
            </Label>
            <Select value={filters.branch || "all"} onValueChange={(v) => updateFilter("branch", v)}>
              <SelectTrigger className="rounded-sm" data-testid="upcoming-filter-branch">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map((b) => <SelectItem key={b.branch_id} value={b.location}>{b.location}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-4">
          <Button
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              setFilters({ dateRange: "", service_type: "", sa: "", source: "", status: "", branch: "" });
            }}
            data-testid="upcoming-filter-clear"
          >
            Clear All
          </Button>
          <Button
            className="bg-black text-white hover:bg-gray-800 rounded-sm"
            onClick={() => setFilterOpen(false)}
            data-testid="upcoming-filter-apply"
          >
            Apply
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-black border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="upcoming-page">
      {/* Standard Header */}
      <PageHeader
        icon={CalendarClock}
        title="Upcoming"
        subtitle="Future scheduled appointments"
        onFilter={() => setFilterOpen(true)}
        filterTestId="upcoming-filter-btn"
        onRefresh={fetchData}
        refreshTestId="upcoming-refresh-btn"
        primaryAction={canCreateAppt ? {
          label: "New Appointment",
          onClick: () => navigate("/new-appointment"),
          testId: "new-appointment-btn",
        } : null}
      />

      {/* Tabs for Tomorrow and Future */}
      <Tabs defaultValue="tomorrow" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2 rounded-sm h-10">
          <TabsTrigger value="tomorrow" className="rounded-sm text-sm uppercase tracking-wider" data-testid="tomorrow-tab">
            Tomorrow ({tomorrowRows.length})
          </TabsTrigger>
          <TabsTrigger value="future" className="rounded-sm text-sm uppercase tracking-wider" data-testid="future-tab">
            Future ({futureRows.length})
          </TabsTrigger>
        </TabsList>

        {/* Tomorrow Tab Content */}
        <TabsContent value="tomorrow" className="mt-4">
          <div className="space-y-3" data-testid="tomorrow-section">
            <div>
              <h2 className="text-xl font-bold">{formatDate(tmrStr)}</h2>
              <p className="text-sm text-gray-500">{tomorrowRows.length} appointment{tomorrowRows.length !== 1 ? "s" : ""} for tomorrow (N-1 &amp; Docket Prep)</p>
            </div>
            <div className="flex items-center justify-between">
              <div></div>
              <ColumnToolbar columnManager={tmrColMgr} />
            </div>
            <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      {visTmr.map((col) => (
                        <TableHead key={col.id} className={`text-xs font-bold uppercase whitespace-nowrap text-center${col.id === "actions" ? " w-12" : ""}`}>{col.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tomorrowRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={visTmr.length} className="text-center text-sm text-gray-400 py-6">No appointments scheduled for tomorrow.</TableCell>
                      </TableRow>
                    ) : (
                      tomorrowRows.map((row) => (
                        <TableRow key={row.appointment_id} className="hover:bg-gray-50">
                          {visTmr.map((col) => (
                            <TableCell key={col.id} className={`whitespace-nowrap text-center${col.id === "customer_name" ? " text-left" : ""}`}>
                              {renderTmrCell(col.id, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </TabsContent>

        {/* Future Tab Content */}
        <TabsContent value="future" className="mt-4">
          <div className="space-y-3" data-testid="future-section">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600 font-medium" data-testid="future-count">
                {futureRows.length} future booking{futureRows.length !== 1 ? "s" : ""}
              </div>
              <ColumnToolbar columnManager={futColMgr} />
            </div>
            <Card className="border border-gray-200 rounded-sm shadow-none overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      {visFut.map((col) => (
                        <TableHead key={col.id} className={`text-xs font-bold uppercase whitespace-nowrap text-center${col.id === "actions" ? " w-12" : ""}`}>{col.label}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {futureRows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={visFut.length} className="text-center text-sm text-gray-400 py-6">No future bookings found.</TableCell>
                      </TableRow>
                    ) : (
                      futureRows.map((row) => (
                        <TableRow key={row.appointment_id} className="hover:bg-gray-50">
                          {visFut.map((col) => (
                            <TableCell key={col.id} className={`whitespace-nowrap text-center${col.id === "customer_name" ? " text-left" : ""}`}>
                              {renderFutCell(col.id, row)}
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Filter Dialog */}
      <FilterDialog />

      {/* Customize + Rearrange modals — one pair per tab, wired to that tab's manager */}
      <ColumnModals
        columnManager={tmrColMgr}
        defaultColumns={UPCOMING_COLUMNS}
        customizableIds={CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS}
      />
      <ColumnModals
        columnManager={futColMgr}
        defaultColumns={UPCOMING_COLUMNS}
        customizableIds={CUSTOMIZABLE_APPOINTMENT_COLUMN_IDS}
      />

      {/* N-1 Status Popup (Rescheduled / Cancelled) */}
      <Dialog open={n1Popup.open} onOpenChange={(open) => { if (!open) setN1Popup({ ...n1Popup, open: false }); }}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="n1-popup-dialog"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          closeClassName="p-1 transition-colors hover:bg-red-600 hover:text-white hover:opacity-100"
          onCloseClick={(e) => {
            if (n1Popup.rescheduleDate || n1Popup.rescheduleRemarks || n1Popup.cancelReason) {
              e.preventDefault();
              setDiscardConfirmOpen(true);
            }
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-lg font-bold uppercase tracking-tight">
              {n1Popup.status === "Rescheduled" ? "Reschedule Appointment" : "Cancel Appointment"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {n1Popup.status === "Rescheduled" && (
              <>
                <div>
                  <Label className="form-label">Reschedule Date</Label>
                  <Input
                    type="date"
                    min={tmrStr}
                    value={n1Popup.rescheduleDate}
                    onChange={(e) => setN1Popup({ ...n1Popup, rescheduleDate: e.target.value })}
                    className="rounded-sm mt-1"
                    data-testid="n1-reschedule-date"
                  />
                </div>
                <div>
                  <Label className="form-label">Remarks</Label>
                  <Textarea
                    placeholder="Reschedule remarks..."
                    value={n1Popup.rescheduleRemarks}
                    onChange={(e) => setN1Popup({ ...n1Popup, rescheduleRemarks: e.target.value })}
                    className="rounded-sm mt-1"
                    data-testid="n1-reschedule-remarks"
                  />
                </div>
              </>
            )}
            {n1Popup.status === "Cancelled" && (
              <div>
                <Label className="form-label">Reason for Cancellation</Label>
                <Textarea
                  placeholder="Enter reason..."
                  value={n1Popup.cancelReason}
                  onChange={(e) => setN1Popup({ ...n1Popup, cancelReason: e.target.value })}
                  className="rounded-sm mt-1"
                  data-testid="n1-cancel-reason"
                />
              </div>
            )}
          </div>
          <DialogFooter className="flex justify-end gap-2 pt-2">
            <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setN1Popup({ ...n1Popup, open: false })} data-testid="n1-popup-cancel">Cancel</Button>
            <Button size="sm" className="rounded-sm bg-black text-white hover:bg-gray-800" onClick={submitN1Popup} disabled={submittingN1} data-testid="n1-popup-submit">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <DiscardConfirm
        open={discardConfirmOpen}
        onOpenChange={setDiscardConfirmOpen}
        onDiscard={() => {
          setDiscardConfirmOpen(false);
          setN1Popup({ ...n1Popup, open: false });
        }}
      />
    </div>
  );
};

export default Upcoming;
